import { useState } from 'react'
import { useCallback } from 'react'
import * as Sentry from '@sentry/nextjs'
import useTranslation from 'next-translate/useTranslation'
import { Controller, useForm } from 'react-hook-form'
import { useTransitionOrderToState } from '@lib/api/cart/transitionOrderToState'
import { useUI } from '@components/ui'
import { FormRadioButton } from '@components/ui/Form/FormRadioButton'
import { StandardButton } from '@components/ui/StandardButton'
import { useSetOrderShippingMethod } from '@lib/api/cart/setOrderShippingMethod'
import { isTranstitionErrorUserNotConnected } from '@lib/api/cart/helpers'
import { Loader } from '@components/icons'

export interface ShippingMethodFormData {
  shippingMethod: string
}

export const ShippingMethodView = () => {
  const [loading, setLoading] = useState(false)
  const { setSidebarView } = useUI()
  const { transitionOrderToState } = useTransitionOrderToState()
  const { t } = useTranslation()
  const { setOrderShippingMethod } = useSetOrderShippingMethod()
  const onExit = useCallback(async () => {
    await transitionOrderToState({
      state: 'EnteringShippingAddress',
    })
  }, [transitionOrderToState])

  const { handleSubmit, control } = useForm<ShippingMethodFormData>({
    mode: 'onSubmit',
  })
  const onSubmit = useCallback(
    async (data: ShippingMethodFormData) => {
      setLoading(true)
      try {
        await setOrderShippingMethod({ shippingMethodId: data.shippingMethod })
      } catch (error) {
        Sentry.captureException(error)
        console.error(error)
        setLoading(false)

        return
      }
      const transitionResult = await transitionOrderToState({
        state: 'ArrangingPayment',
      })
      if (isTranstitionErrorUserNotConnected(transitionResult)) {
        setSidebarView('WALLET_VIEW', () => onSubmit(data))
      }
      setLoading(false)
    },
    [setOrderShippingMethod, transitionOrderToState, setSidebarView]
  )

  return (
    <div>
      <h2 className="text-blue text-4xl font-archivoBlack mt-2 mb-9 sm:my-9">
        {t('checkout:shipping_method')}
      </h2>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          control={control}
          name="shippingMethod"
          defaultValue="1"
          render={({ field }) => (
            <FormRadioButton
              label={`${t('checkout:free_delivery_method')}`}
              hint={t('checkout:free')}
              {...field}
              checkedValue={field.value}
              value="1"
            />
          )}
        />
        <div className="flex flex-col lg:flex-row-reverse items-center mt-10">
          <div className="mx-auto lg:m-1 lg:ml-4 w-[283px]">
            <StandardButton
              type="submit"
              label={
                loading ? <Loader className="m-auto" /> : t('checkout:payment')
              }
              disabled={loading}
              onClick={() => {}}
            />
          </div>
          <button
            type="button"
            className="mx-auto lg:ml-0 lg:mr-auto mb-auto h-16 mt-6 lg:mt-0 whitespace-nowrap"
            onClick={onExit}
          >
            {`< ${t('checkout:back_to_shipping_address')}`}
          </button>
        </div>
      </form>
    </div>
  )
}
